import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Avatar,
  Paper,
  TablePagination,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as CompanyIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Instagram,
  Facebook,
  TikTok,
  Payment as PaymentIcon,
  Message as MessageIcon,
  VideoLibrary as VideoLibraryIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow, format } from 'date-fns';
import DashboardLayout from '../components/DashboardLayout';
import affiliateService from '../services/affiliateService';

const Affiliates = () => {
  const { user: currentUser } = useAuth();

  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verifyingId, setVerifyingId] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [affiliateToDelete, setAffiliateToDelete] = useState(null);
  const [videosDialogOpen, setVideosDialogOpen] = useState(false);
  const [selectedAffiliateVideos, setSelectedAffiliateVideos] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    hasVerificationToken: false,
    profileImage: null,
    profileImagePreview: null,
    affiliateData: {
      status: 'Pending',
      company: '',
      name: '',
      address: '',
      city: '',
      country: '',
      phone: '',
      instagram: '',
      tiktok: '',
      facebook: '',
      other: '',
      bestDM: '',
      paymentDetails: ''
    }
  });

  // Fetch affiliates from API
  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      const data = await affiliateService.getAffiliates();
      setAffiliates(data);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      setError('Failed to load affiliates');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddAffiliate = () => {
    setEditingAffiliate(null);
    setError(''); // Clear any previous errors
    setFormData({
      email: '',
      username: '',
      password: '',
      profileImage: null,
      profileImagePreview: null,
      affiliateData: {
        status: 'Active',
        company: '',
        name: '',
        address: '',
        city: '',
        country: '',
        phone: '',
        instagram: '',
        tiktok: '',
        facebook: '',
        other: '',
        bestDM: '',
        paymentDetails: ''
      },
      hasVerificationToken: false
    });
    setDialogOpen(true);
  };

  const handleEditAffiliate = (affiliate) => {
    setEditingAffiliate(affiliate);
    setError(''); // Clear any previous errors
    setFormData({
      email: affiliate.email,
      username: affiliate.username || '',
      password: '', // Clear password field for security
      profileImage: null,
      profileImagePreview: affiliate.profileIcon?.path || null,
      affiliateData: {
        status: affiliate.affiliateData?.status || 'Pending',
        company: affiliate.affiliateData?.company || '',
        name: affiliate.affiliateData?.name || '',
        address: affiliate.affiliateData?.address || '',
        city: affiliate.affiliateData?.city || '',
        country: affiliate.affiliateData?.country || '',
        phone: affiliate.affiliateData?.phone || '',
        instagram: affiliate.affiliateData?.instagram || '',
        tiktok: affiliate.affiliateData?.tiktok || '',
        facebook: affiliate.affiliateData?.facebook || '',
        other: affiliate.affiliateData?.other || '',
        bestDM: affiliate.affiliateData?.bestDM || '',
        paymentDetails: affiliate.affiliateData?.paymentDetails || ''
      },
      hasVerificationToken: !!affiliate.verificationToken
    });
    setDialogOpen(true);
  };

  const handleDeleteAffiliate = (affiliate) => {
    setAffiliateToDelete(affiliate);
    setDeleteDialogOpen(true);
  };

  const handleSaveAffiliate = async (directCreate = false) => {
    // Validate required fields (company and name are now optional)
    const missingFields = [];

    if (!editingAffiliate && (!formData.email || formData.email.trim() === '')) {
      missingFields.push('Email');
    }

    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      setTimeout(() => setError(''), 5000);
      return;
    }

    try {
      setLoading(true);

      if (editingAffiliate) {
        // Update existing affiliate
        const payload = new FormData();
        payload.append('affiliateData', JSON.stringify(formData.affiliateData));
        payload.append('username', formData.username);

        // Only include password if it was entered
        if (formData.password && formData.password.trim() !== '') {
          payload.append('password', formData.password);
        }

        // Add profile image if a NEW one was selected
        if (formData.profileImage) {
          payload.append('profileImage', formData.profileImage);
        }

        await affiliateService.updateAffiliate(editingAffiliate._id, payload);
        setSuccess('Affiliate updated successfully');
      } else {
        // Create new affiliate
        const payload = new FormData();
        payload.append('email', formData.email);
        payload.append('username', formData.username);
        payload.append('password', formData.password);
        payload.append('name', formData.affiliateData.name);
        if (directCreate) {
          payload.append('verified', 'true');
        }

        if (formData.profileImage) {
          payload.append('profileImage', formData.profileImage);
        }

        await affiliateService.createAffiliate(payload);
        if (directCreate) {
          setSuccess('Affiliate account created successfully and is now active.');
        } else {
          setSuccess('Affiliate created successfully. Verification email sent.');
        }
      }

      // Refresh the list
      await fetchAffiliates();
      setError(''); // Clear error on successful save
      setDialogOpen(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving affiliate:', error);
      setError('Error saving affiliate. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await affiliateService.resendVerification(formData.email);
      setSuccess('Verification email resent successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error resending verification:', error);
      setError('Error resending verification email. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Same as handleResendVerification, but callable directly from a list row
  // (no need to open the edit dialog first). Uses its own loading flag so it
  // doesn't interact with the edit dialog's `loading` state.
  const handleResendVerificationForRow = async (affiliate) => {
    setVerifyingId(affiliate._id);
    try {
      await affiliateService.resendVerification(affiliate.email);
      setSuccess(`Verification email resent to ${affiliate.email}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error resending verification:', error);
      setError('Error resending verification email. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setVerifyingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await affiliateService.deleteAffiliate(affiliateToDelete._id);
      await fetchAffiliates(); // Refresh the list
      setDeleteDialogOpen(false);
      setAffiliateToDelete(null);
      setSuccess('Affiliate deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting affiliate:', error);
      setError('Error deleting affiliate. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Banned':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleInputChange = (field) => (event) => {
    if (field === 'email' || field === 'username' || field === 'password') {
      setFormData(prev => ({
        ...prev,
        [field]: event.target.value
      }));
    } else if (field === 'profileImage') {
      const file = event.target.files[0];
      if (file) {
        setFormData(prev => ({
          ...prev,
          profileImage: file,
          profileImagePreview: URL.createObjectURL(file)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        affiliateData: {
          ...prev.affiliateData,
          [field]: event.target.value
        }
      }));
    }
  };

  // Paginated affiliates
  const paginatedAffiliates = affiliates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DashboardLayout title="Affiliate Partners">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Manage your affiliate partners and their contact information
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Affiliates
                </Typography>
                <Typography variant="h4">
                  {affiliates.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Active
                </Typography>
                <Typography variant="h4" color="success.main">
                  {affiliates.filter(a => a.affiliateData?.status === 'Active').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Pending
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {affiliates.filter(a => a.affiliateData?.status === 'Pending').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Banned
                </Typography>
                <Typography variant="h4" color="error.main">
                  {affiliates.filter(a => a.affiliateData?.status === 'Banned').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Affiliates Table */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Affiliate Partners</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddAffiliate}
              >
                New Affiliate
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
              <Table sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Profile Image</TableCell>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Company</TableCell>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Location</TableCell>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Contact</TableCell>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Social Media</TableCell>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Best DM</TableCell>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Payment</TableCell>
                    <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Sample Videos</TableCell>
                    <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAffiliates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography color="text.secondary">
                          No affiliate partners found. Add your first affiliate!
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedAffiliates.map((affiliate, index) => (
                      <TableRow
                        key={affiliate._id}
                        sx={{
                          '&:nth-of-type(even)': { backgroundColor: 'grey.25' },
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Chip
                              label={affiliate.affiliateData?.status || 'Pending'}
                              color={getStatusColor(affiliate.affiliateData?.status || 'Pending')}
                              size="small"
                            />
                            {affiliate.verificationToken && (
                              <Chip
                                label="Email Unverified"
                                color="warning"
                                variant="outlined"
                                size="small"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Avatar
                            src={affiliate.profileIcon?.path}
                            sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}
                          >
                            {!affiliate.profileIcon?.path && <PersonIcon fontSize="small" />}
                          </Avatar>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {affiliate.affiliateData?.company || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {affiliate.affiliateData?.name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="body2">
                            {affiliate.affiliateData?.city && affiliate.affiliateData?.country
                              ? `${affiliate.affiliateData.city}, ${affiliate.affiliateData.country}`
                              : '-'
                            }
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {affiliate.affiliateData?.address || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Link href={`mailto:${affiliate.email}`} variant="body2" underline="hover">
                              {affiliate.email}
                            </Link>
                            <Typography variant="caption" color="text.secondary">
                              {affiliate.affiliateData?.phone || '-'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {affiliate.affiliateData?.instagram && (
                              <Chip
                                icon={<Instagram fontSize="small" />}
                                label={affiliate.affiliateData.instagram}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {affiliate.affiliateData?.tiktok && (
                              <Chip
                                label={affiliate.affiliateData.tiktok}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {affiliate.affiliateData?.facebook && (
                              <Chip
                                icon={<Facebook fontSize="small" />}
                                label={affiliate.affiliateData.facebook}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="body2">
                            {affiliate.affiliateData?.bestDM || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {affiliate.affiliateData?.paymentDetails || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          {affiliate.sampleVideos && affiliate.sampleVideos.length > 0 ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => {
                                setSelectedAffiliateVideos(affiliate.sampleVideos);
                                setVideosDialogOpen(true);
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              <VideoLibraryIcon fontSize="small" />
                              View Videos ({affiliate.sampleVideos.length})
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No videos
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                          {affiliate.verificationToken && (
                            <Tooltip title={`Resend verification email to ${affiliate.email}`}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleResendVerificationForRow(affiliate)}
                                  disabled={verifyingId === affiliate._id}
                                  color="warning"
                                >
                                  {verifyingId === affiliate._id
                                    ? <CircularProgress size={18} color="inherit" />
                                    : <EmailIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit Affiliate">
                            <IconButton
                              size="small"
                              onClick={() => handleEditAffiliate(affiliate)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Affiliate">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAffiliate(affiliate)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={affiliates.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>

        {/* Add/Edit Affiliate Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingAffiliate ? 'Edit Affiliate Partner' : 'Add New Affiliate Partner'}
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {editingAffiliate ? (
                  // Edit Mode
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={formData.affiliateData.status}
                          onChange={handleInputChange('status')}
                          label="Status"
                        >
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Pending">Pending</MenuItem>
                          <MenuItem value="Banned">Banned</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Company"
                        value={formData.affiliateData.company}
                        onChange={handleInputChange('company')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={formData.affiliateData.name}
                        onChange={handleInputChange('name')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email *"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        required
                        disabled={true}
                      />
                      {formData.hasVerificationToken && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={handleResendVerification}
                            disabled={loading}
                          >
                            Resend Verification Email
                          </Button>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Username"
                        value={formData.username}
                        onChange={handleInputChange('username')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="New Password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange('password')}
                        placeholder="Leave blank to keep current"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        value={formData.affiliateData.address}
                        onChange={handleInputChange('address')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Profile Image
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={formData.profileImagePreview}
                            sx={{ width: 64, height: 64, border: '1px solid', borderColor: 'divider' }}
                          >
                            {!formData.profileImagePreview && <PersonIcon />}
                          </Avatar>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                          >
                            {formData.profileImagePreview ? 'Change Image' : 'Upload Image'}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleInputChange('profileImage')}
                            />
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={formData.affiliateData.city}
                        onChange={handleInputChange('city')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={formData.affiliateData.country}
                        onChange={handleInputChange('country')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={formData.affiliateData.phone}
                        onChange={handleInputChange('phone')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Best DM Method"
                        value={formData.affiliateData.bestDM}
                        onChange={handleInputChange('bestDM')}
                        placeholder="e.g., Instagram, Email, TikTok"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Instagram"
                        value={formData.affiliateData.instagram}
                        onChange={handleInputChange('instagram')}
                        placeholder="@username"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="TikTok"
                        value={formData.affiliateData.tiktok}
                        onChange={handleInputChange('tiktok')}
                        placeholder="@username"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Facebook"
                        value={formData.affiliateData.facebook}
                        onChange={handleInputChange('facebook')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Other Social Media"
                        value={formData.affiliateData.other}
                        onChange={handleInputChange('other')}
                        placeholder="YouTube, LinkedIn, etc."
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Payment Details"
                        value={formData.affiliateData.paymentDetails}
                        onChange={handleInputChange('paymentDetails')}
                        multiline
                        rows={2}
                        placeholder="PayPal email, bank details, etc."
                      />
                    </Grid>
                  </>
                ) : (
                  // Add Mode - Show requested fields: Email, Password, Name, Username
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={formData.affiliateData.name}
                        onChange={handleInputChange('name')}
                        required
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Username"
                        value={formData.username}
                        onChange={handleInputChange('username')}
                        required
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        required
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange('password')}
                        required
                        autoComplete="new-password"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Profile Image
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={formData.profileImagePreview}
                            sx={{ width: 64, height: 64, border: '1px solid', borderColor: 'divider' }}
                          >
                            {!formData.profileImagePreview && <PersonIcon />}
                          </Avatar>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                          >
                            Upload Image
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleInputChange('profileImage')}
                            />
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setDialogOpen(false);
                setError(''); // Clear error when closing dialog
              }}
              variant="contained"
              data-cancel="true"
              sx={{ bgcolor: 'grey.500', '&:hover': { bgcolor: 'grey.600' } }}
            >
              Cancel
            </Button>
            {!editingAffiliate && (
              <Button
                onClick={() => handleSaveAffiliate(true)}
                variant="contained"
                disabled={loading}
                color="success"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                Create Account Now
              </Button>
            )}
            <Button
              onClick={() => handleSaveAffiliate(false)}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {editingAffiliate ? 'Update Affiliate' : 'Verify Email'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>
            Delete Affiliate Partner
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>{affiliateToDelete?.affiliateData?.name}</strong> from <strong>{affiliateToDelete?.affiliateData?.company}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant="contained"
              data-cancel="true"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sample Videos Dialog */}
        <Dialog
          open={videosDialogOpen}
          onClose={() => setVideosDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VideoLibraryIcon />
              <Typography variant="h6">Sample Videos</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedAffiliateVideos && selectedAffiliateVideos.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {selectedAffiliateVideos.map((video, index) => (
                    <Grid item xs={12} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ mb: 2 }}>
                            <video
                              src={video.fileUrl}
                              controls
                              style={{
                                width: '100%',
                                maxHeight: '400px',
                                borderRadius: '4px'
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {video.fileName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Size: {(video.fileSize / (1024 * 1024)).toFixed(2)} MB
                            </Typography>
                            {video.uploadDate && (
                              <Typography variant="caption" color="text.secondary">
                                Uploaded: {format(new Date(video.uploadDate), 'PPp')}
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No sample videos available
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setVideosDialogOpen(false)}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </DashboardLayout >
  );
};

export default Affiliates; 